import { Controller } from "@hotwired/stimulus"

// Drives the "Attach files" picker on the support reply form. Each selected file
// is uploaded to the server-side proxy (Admin::Help::UploadsController), which
// stores it in the private support bucket and returns its metadata. On success we
// render a removable chip and append the hidden
// help_message[attachments_attributes][<i>][...] inputs so the file rides along
// as a Help::MessageAttachment when the reply is submitted.
export default class extends Controller {
  static targets = ["input", "chips", "fields"]
  // fieldName lets a form choose the param prefix: the staff/student reply nests
  // under help_message[attachments_attributes] (nested attributes); the guided
  // new-ticket form (no help_message wrapper yet) uses a plain `attachments`.
  static values = { uploadUrl: String, fieldName: { type: String, default: "help_message[attachments_attributes]" } }

  connect() {
    this.index = 0
  }

  upload() {
    const files = Array.from(this.inputTarget.files || [])
    files.forEach((file) => this.uploadFile(file))
    // Reset so re-selecting the same file fires `change` again.
    this.inputTarget.value = ""
  }

  async uploadFile(file) {
    const chip = this.buildChip(file.name)
    this.chipsTarget.appendChild(chip)

    try {
      const form = new FormData()
      form.append("file", file)

      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" },
        body: form,
      })

      if (!response.ok) throw new Error(`Upload failed (${response.status})`)

      const data = await response.json()
      this.appendFields(data)
      chip.classList.remove("text-bg-secondary")
      chip.classList.add("text-bg-light", "border")
    } catch (error) {
      chip.classList.remove("text-bg-secondary")
      chip.classList.add("text-bg-danger")
      chip.querySelector(".chip-label").textContent = `${file.name} — failed`
    }
  }

  buildChip(filename) {
    const chip = document.createElement("span")
    chip.className = "badge text-bg-secondary d-inline-flex align-items-center gap-2"
    chip.innerHTML = '<i class="bi bi-paperclip"></i><span class="chip-label"></span>'
    chip.querySelector(".chip-label").textContent = filename
    return chip
  }

  appendFields(data) {
    const i = this.index++
    const wrapper = document.createElement("div")
    wrapper.appendChild(this.hiddenField(i, "filename", data.filename))
    wrapper.appendChild(this.hiddenField(i, "s3_key", data.s3_key))
    wrapper.appendChild(this.hiddenField(i, "content_type", data.content_type))
    wrapper.appendChild(this.hiddenField(i, "byte_size", data.byte_size))
    this.fieldsTarget.appendChild(wrapper)
  }

  hiddenField(index, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = `${this.fieldNameValue}[${index}][${name}]`
    input.value = value
    return input
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
